import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Save, Plus, Search, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, Package, FileText, X,
  ListChecks, Hash, Link2, Weight, Boxes, Layers,
  Send
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';

import { getNewLoadList, createNewLoad } from '@/redux/Load/actions';
import PageHeader from '../FormHeader/formPageHeader';

/* ─────────────────────────────────────────────────────────────────────────────
   ALL ORIGINAL LOGIC IS UNTOUCHED — only JSX / classNames are changed
───────────────────────────────────────────────────────────────────────────── */

const LoadPlanForm = ({ onBack, planData = null }) => {
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const { newLoadList } = useSelector((state) => state.load);
  const dispatch = useDispatch();
  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
  const authName = authUser?.username;
  const companyId = authUser?.companyId;

  useEffect(() => {
    _getLoadList(1);
  }, [searchText]);

  useEffect(() => {
    setItems(newLoadList?.data || []);
  }, [newLoadList]);

  const _getLoadList = async (page = 1) => {
    setCurrentPage(page);
    const payload = {
      IPageNo: page, IPageRecords: recordsPerPage, FromDate: '', ToDate: '',
      SearchKeyword: searchText, compnayId: companyId
    };
    await dispatch(getNewLoadList(payload));
  };

  const handlePrevious = () => {
    if (currentPage > 1) _getLoadList((prev) => prev - 1);
  };

  const handleNext = () => {
    if (pagination?.nextPage > 0) _getLoadList(pagination.nextPage);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    if (field === 'loadingUnit') {
      updated[index][field] = parseFloat(value);
      const casesPerUnit   = parseFloat(updated[index].casesCount) || 0;
      const remainingUnit  = parseFloat(updated[index].remainingUnit) || 0;
      updated[index].loadingCases = (parseFloat(value) * casesPerUnit);
    }
    console.log('Updated Item:', updated);
    setItems(updated);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setItems(items.map(item => ({ ...item, select: checked })));
  };

  const handleSelectItem = (index) => {
    const updated = [...items];
    updated[index].select = !updated[index].select;
    setItems(updated);
    setSelectAll(updated.every(item => item.select));
  };

  const addItem = () => {
    setItems([...items, {
      id: items.length + 1, sno: items.length + 1, select: false,
      soId: '', sku: '', totalContUnit: '0.00', remainingContUnit: '0.00',
      loadingUnit: '', casesPerUnit: '0.00', totalCases: '0.00',
      remainingCases: '0.00', loadingCases: '', totalQty: '0.00',
      uom: 'Case', packing: '0', packingUnit: 'TON', linkPoItem: ''
    }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let selectedItems = items.filter(item => item.select);
    if (selectedItems.length === 0) {
      alert('Please select at least one item to create a load plan.');
      return;
    }
    let selectedPonumber = items.filter(item => item.select && (item?.poNumberSelected === '' || item?.poNumberSelected == null));
    if (selectedPonumber.length > 0) {
      alert('Please select PO Number for all selected items.');
      return;
    }
    console.log('Load Plan Data:', items);
    await Promise.all(items.map(async item => {
      if (item.select) {
        const payload = {
          "soId": item?.salesorderId,
          "soLineitemId": item?.salesorderLineitemId,
          "soNumber": item?.salesordernumber,
          "poNumber": item?.poNumberSelected,
          "sku": item?.sku,
          "quantity": item?.loadingUnit,
          "createdBy": "admin"
        };
        console.log('Payload:', payload);
        const dd = await createNewLoad(payload);
      }
    }));
    window.location.reload();
  };

  /* ── helpers ── */
  const selectedCount = items.filter(i => i.select).length;

  /* shared input/select classes */
  const cellInput  = 'w-full min-w-[64px] px-2 py-1.5 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all';
  const cellSelect = 'w-full min-w-[80px] px-2 py-1.5 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer';
  const readCell   = 'text-[12.5px] font-medium text-slate-700 dark:text-slate-300 tabular-nums';

  /* ── column header definitions ── */
  const HEADERS = [
    { label: '#',                  icon: null,     cls: 'w-8'     },
    { label: '',                   icon: null,     cls: 'w-8'     }, // checkbox
    { label: 'SO ID',              icon: Hash,     cls: ''        },
    { label: 'SKU',                icon: Package,  cls: ''        },
    { label: 'Total Cont Unit',    icon: Layers,   cls: ''        },
    { label: 'Rem. Cont Unit',     icon: Layers,   cls: ''        },
    { label: 'Loading Unit',       icon: Weight,   cls: 'w-24'   },
    { label: 'Cases / Unit',       icon: Boxes,    cls: ''        },
    { label: 'Total Cases',        icon: Boxes,    cls: ''        },
    { label: 'Rem. Cases',         icon: Boxes,    cls: ''        },
    { label: 'Loading Cases',      icon: Boxes,    cls: 'w-24'   },
    { label: 'Total Qty',          icon: Weight,   cls: ''        },
    { label: 'UOM',                icon: null,     cls: 'w-24'   },
    { label: 'Packing',            icon: null,     cls: 'w-32'   },
    { label: 'Link PO Item',       icon: Link2,    cls: 'w-36'   },
  ];

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="h-full bg-slate-50 dark:bg-slate-950">

      {/* ── STICKY PAGE HEADER ── */}
      <div className="page-header-sticky sticky top-0 z-50">
        <PageHeader
          icon={<FileText className="w-5 h-5 text-white" />}
          title="New Load Plan"
          subtitle="Select sales order lines and allocate loading units"
          actions={[
            { label: 'Back', icon: <ArrowLeft className="w-4 h-4" />, onClick: onBack, variant: 'ghost' },
          ]}
          onSubmit={handleSubmit}
          submitLabel="Submit Plan"
          submitFormId="lp-form"
        />
        <div style={{ height: 'var(--page-header-height)' }} />
      </div>

      <div className="p-4 space-y-3 pb-16">

        {/* ── TABLE CARD — styled like RecentFilingsTable ── */}
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-gray-700/30 dark:border-slate-700 shadow-sm dark:shadow-slate-900/40 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">

          {/* Card header */}
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white flex items-center justify-center shadow-md">
                <ListChecks className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-slate-900 dark:text-white">
                  Sales Order Lines
                </div>
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
                  Select items and enter loading quantities to generate a load plan
                </div>
              </div>
            </div>

            {/* Right: search + selected badge */}
            <div className="flex items-center gap-2">
              {selectedCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[12px] font-semibold ring-1 ring-blue-200 dark:ring-blue-700/50">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  {selectedCount} selected
                </span>
              )}
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search SO, SKU…"
                  className="h-9 pl-8 pr-3 w-52 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── TABLE ── */}
          <form id="lp-form" onSubmit={handleSubmit}>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">

                {/* THEAD — sticky, backdrop-blur, same as CustomsFiling */}
                <thead className="sticky top-0 z-10 bg-slate-50/90 dark:bg-slate-800/80 backdrop-blur text-[10.5px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    {/* # */}
                    <th className="text-center font-semibold px-4 py-3 w-10">
                      #
                    </th>
                    {/* Select All */}
                    <th className="px-3 py-3 w-9">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-3 h-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-center font-semibold px-3 py-3">SO ID</th>
                    <th className="text-center font-semibold px-3 py-3">SKU</th>
                    <th className="text-center font-semibold px-3 py-3">Total Cont Unit</th>
                    <th className="text-center font-semibold px-3 py-3">Rem. Cont Unit</th>
                    <th className="text-center font-semibold px-3 py-3">Loading Unit</th>
                    <th className="text-center font-semibold px-3 py-3">Cases / Unit</th>
                    <th className="text-center font-semibold px-3 py-3">Total Cases</th>
                    <th className="text-center font-semibold px-3 py-3">Rem. Cases</th>
                    <th className="text-center font-semibold px-3 py-3">Loading Cases</th>
                    <th className="text-center font-semibold px-3 py-3">Total Qty</th>
                    <th className="text-center font-semibold px-3 py-3 w-24">UOM</th>
                    <th className="text-center font-semibold px-3 py-3 w-36">Packing</th>
                    <th className="text-center font-semibold px-4 py-3 w-40">Link PO Item</th>
                  </tr>
                </thead>

                {/* TBODY */}
                <tbody>
                  {items?.map((item, index) => {
                    const isSelected = !!item?.select;
                    return (
                      <tr
                        key={item.id ?? index}
                        className={[
                          'border-t border-slate-100 dark:border-slate-700/50 transition-colors',
                          isSelected
                            ? 'bg-blue-50/60 dark:bg-blue-900/15'
                            : index % 2 === 1
                              ? 'bg-slate-50/50 dark:bg-slate-800/20'
                              : '',
                          'hover:bg-blue-50/40 dark:hover:bg-blue-900/10',
                        ].filter(Boolean).join(' ')}
                      >
                        {/* Row number */}
                        <td className="px-4 py-2.5 text-[11px] font-medium text-slate-400 dark:text-slate-500 tabular-nums w-10">
                          {item?.rowNum ?? index + 1}
                        </td>

                        {/* Checkbox */}
                        <td className="px-3 py-2.5 w-9">
                          <input
                            type="checkbox"
                            checked={!!item?.select}
                            onChange={() => handleSelectItem(index)}
                            className="w-3 h-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </td>

                        {/* SO ID */}
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                            <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono text-[12px]">
                              {item?.salesordernumber ?? '—'}
                            </span>
                          </span>
                        </td>

                        {/* SKU */}
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center px-2 h-6 rounded-md  text-slate-700 dark:text-slate-300 font-mono text-[11px] font-semibold ">
                            {item?.sku ?? '—'}
                          </span>
                        </td>

                        {/* Total Cont Unit */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={readCell}>{item?.uot ?? '—'}</span>
                        </td>

                        {/* Rem. Cont Unit */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={[
                            readCell,
                            parseFloat(item?.remainingUnit) === 0
                              ? 'text-rose-500 dark:text-rose-400'
                              : parseFloat(item?.remainingUnit) < 10
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-emerald-600 dark:text-emerald-400'
                          ].join(' ')}>
                            {item?.remainingUnit ?? '—'}
                          </span>
                        </td>

                        {/* Loading Unit — editable */}
                        <td className="px-3 py-2.5 text-right">
                          <input
                            type="number"
                            max={item?.remainingUnit ?? ''}
                            min={0}
                            value={item?.loadingUnit}
                            onChange={(e) =>
                              item?.remainingUnit >= e.target.value
                                ? handleItemChange(index, 'loadingUnit', e.target.value)
                                : null
                            }
                            className="w-20 px-2 py-1.5 text-xs text-right rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all tabular-nums"
                          />
                        </td>

                        {/* Cases / Unit */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={readCell}>{item?.casesPerUot ?? '—'}</span>
                        </td>

                        {/* Total Cases */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={readCell}>{item?.casesCount ?? '—'}</span>
                        </td>

                        {/* Rem. Cases */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={readCell}>{item?.remainingCases ?? '—'}</span>
                        </td>

                        {/* Loading Cases — computed, readOnly */}
                        <td className="px-3 py-2.5 text-right">
                          <input
                            type="number"
                            readOnly
                            value={item?.loadingCases ?? ''}
                            onChange={(e) => handleItemChange(index, 'loadingCases', e.target.value)}
                            className="w-20 px-2 py-1.5 text-xs text-right rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 cursor-default tabular-nums focus:outline-none"
                          />
                        </td>

                        {/* Total Qty */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={readCell}>{item?.quantity ?? '—'}</span>
                        </td>

                        {/* UOM */}
                        <td className="px-3 py-2.5">
                          <select
                            value={item?.uom}
                            onChange={(e) => handleItemChange(index, 'uom', e.target.value)}
                            className={cellSelect + ' w-20'}
                          >
                            <option>Case</option>
                            <option>Pallet</option>
                            <option>Box</option>
                            <option>Unit</option>
                          </select>
                        </td>

                        {/* Packing */}
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={item?.packing}
                              onChange={(e) => handleItemChange(index, 'packing', e.target.value)}
                              className="w-12 px-2 py-1.5 text-xs text-right rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all tabular-nums"
                            />
                            <select
                              value={item?.metricType}
                              onChange={(e) => handleItemChange(index, 'metricType', e.target.value)}
                              className={cellSelect + ' w-16'}
                            >
                              <option>TON</option>
                              <option>KG</option>
                              <option>LB</option>
                            </select>
                          </div>
                        </td>

                        {/* Link PO Item */}
                        <td className="px-4 py-2.5">
                          <select
                            value={item?.poNumberSelected}
                            onChange={(e) => handleItemChange(index, 'poNumberSelected', e.target.value)}
                            className={cellSelect + ' w-36'}
                          >
                            <option key="" value="">Select PO Item</option>
                            {item?.poNumbers?.map((poItem, poIndex) => (
                              <option
                                key={poIndex}
                                value={poItem?.poNumber}
                                disabled={parseFloat(poItem?.poUnits) === 0}
                              >
                                {poItem?.poNumber} (QTY: {parseFloat(poItem?.poUnits).toFixed(2)})
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Empty state */}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={15} className="py-16 text-center">
                        <div className="flex flex-col items-center gap-3 text-slate-400 dark:text-slate-600">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Package className="w-6 h-6" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-500 dark:text-slate-400">No items found</div>
                            <div className="text-xs text-slate-400 dark:text-slate-600 mt-0.5">Try adjusting your search</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── PAGINATION FOOTER — styled like RecentFilingsTable footer ── */}
            <div className="border-t border-slate-100 dark:border-slate-700 px-5 py-3 flex items-center justify-between bg-slate-50/60 dark:bg-slate-800/40">
              <div className="flex items-center gap-2 text-[12px] text-slate-500 dark:text-slate-400">
                Showing
                <span className="font-semibold text-slate-700 dark:text-slate-300">{items.length}</span>
                items
                {selectedCount > 0 && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">·</span>
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">{selectedCount} selected</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => _getLoadList(1)}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>

                <span className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[12px] font-semibold border border-blue-100 dark:border-blue-800/50">
                  Page {currentPage}
                </span>

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!newLoadList?.pagination?.nextPage}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </form>
        </section>

        {/* ── BOTTOM ACTION BAR (exact PO / LoadPlanEdit style) ── */}
        <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Select at least <span className="text-red-500 font-semibold">1 item</span> and link a PO before saving
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Cancel</span>
              </button>

              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

              <button
                type="submit"
                form="lp-form"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 hover:bg-green-700 text-white"
              >
                <Send className="w-4 h-4" />
                Submit Plan
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoadPlanForm;